import { FunctionComponent, useMemo, useState, lazy, Suspense, useEffect } from 'react';
import {
    hooks as commonHooks,
    types as commonTypes,
    hooks,
    components as commonComponents,
    annotations,
    utils as commonUtils,
} from '@sitebuilder/common';
import { css } from '@emotion/react';
import { types as repr } from '@sitebuilder/core';
import dataContextStyles from './DataContext.styles';

const CONTENTS_PLACEHOLDER_ID = 'children';

// Global store for SSR to prevent duplicate queries
const queryExecutionTracker = new Map<string, Promise<repr.GraphQLQueryResult | null>>();

const DataContextComp: FunctionComponent<
    React.PropsWithChildren<commonTypes.WidgetInstanceProps>
> = (props) => {
    const appContext = commonHooks.useAppContext();
    const {
        Placeholder,
        inEditMode,
        document,
        accessToken,
        originalHostname,
        queries,
        baseUrl,
        graphQLClient,
    } = appContext;
    const { data, widgetInstanceId } = props;
    const widgetInstances = document?.widgetInstances;
    const widgetInstance = ((widgetInstanceId && widgetInstances) || {})[widgetInstanceId || ''];
    const queryApiClient = graphQLClient;

    const transform = widgetInstance?.properties?.transform as string;
    const extensions: Record<string, unknown> = {};

    if (transform) {
        extensions.transform = transform;
    }

    const variablesStr = widgetInstance?.properties?.queryVariables as string;
    let variables = '';
    const transformFunctionName = `${widgetInstanceId}-queryVariables-transform`;

    try {
        if (typeof variablesStr === 'string' && variablesStr.length > 0) {
            variables = commonUtils.transformDataWithGlobals(
                transformFunctionName,
                variablesStr,
                data
            );
        }
    } catch (e) {
        console.log(e);
    }

    const queryParams = useMemo(
        () => ({
            dataSourceType: widgetInstance?.properties?.dataSourceType as string,
            dataSetName: widgetInstance?.properties?.dataSource as string,
            authorizationToken: accessToken as string,
            query: widgetInstance?.properties?.query as string,
            variables,
            extensions: extensions as Record<string, unknown>,
            originalHostname,
            baseUrl,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [widgetInstance, accessToken, baseUrl, originalHostname, variables]
    );

    const queryId = commonUtils.hashObject(queryParams);
    const existingQuery = queries[queryId];

    const [queryResult, setQueryResult] = useState<repr.GraphQLQueryResult | null>(
        existingQuery?.graphQLQueryResult || null
    );

    const [previousQueryResult, setPreviousQueryResult] = useState<repr.GraphQLQueryResult | null>(
        queryResult
    );

    useEffect(() => {
        if (queryId) {
            setQueryResult(queries?.[queryId]?.graphQLQueryResult);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryId]);

    const updateQueries = (queryId: string, queryData: any) => {
        appContext.setters.setQueries((prevQueries: commonComponents.AppContext.Queries) => ({
            ...prevQueries,
            [queryId]: queryData,
        }));
    };

    const executeQuery = async () => {
        if (queryExecutionTracker.has(queryId)) {
            // Use the ongoing or completed promise
            return queryExecutionTracker.get(queryId)!;
        }

        // Create and track the query promise
        const queryPromise = (async () => {
            try {
                // Ensure queryParams and datasetName exist before making the request
                if (!queryParams || !queryParams.dataSetName) {
                    updateQueries(queryId, {
                        isQueryLoading: false,
                    });

                    // Return a default safe structure to prevent crashes
                    return { data: {}, errors: [{ message: 'Missing queryParams' }] };
                }

                const graphQLQueryResult = await queryApiClient.executeQuery(queryParams);
                if (graphQLQueryResult?.data) {
                    if (Array.isArray(graphQLQueryResult.data.annotations)) {
                        appContext.setters.setAnnotations(
                            annotations.annotator.Annotator.addAnnotations(
                                graphQLQueryResult.data.annotations,
                                appContext.annotations
                            )
                        );
                    }

                    updateQueries(queryId, {
                        isQueryLoading: false,
                        graphqlQueryParams: queryParams,
                        graphQLQueryResult: graphQLQueryResult,
                    });
                }

                return graphQLQueryResult;
            } catch (error) {
                console.error('Error executing query:', error);
                return null;
            }
        })();

        queryExecutionTracker.set(queryId, queryPromise);
        return queryPromise;
    };

    const [sseQueryResult]: any = hooks.useSSE2.useSSE(async () => {
        let gQLQueryResult = null;
        if (!queryResult) {
            gQLQueryResult = await executeQuery();
            setQueryResult(gQLQueryResult);
        }
        return gQLQueryResult;
    }, [queryId]);

    const finalQueryResult = sseQueryResult || queryResult;

    useEffect(() => {
        if (finalQueryResult) {
            setPreviousQueryResult(finalQueryResult);
        }
    }, [finalQueryResult]);

    const instanceData: any = useMemo(() => {
        const tmp = [(finalQueryResult || previousQueryResult)?.data as Record<string, unknown>];
        if (tmp.length === 1 && !tmp[0]) {
            tmp.pop();
        }
        return tmp?.length ? tmp : [{}];
    }, [finalQueryResult, previousQueryResult]);

    return finalQueryResult || previousQueryResult ? (
        <>
            {inEditMode && (
                <div className="data-context">
                    <div className="data-context-title">Data Context</div>
                    <div className="data-context-content"></div>
                </div>
            )}
            {widgetInstanceId &&
                instanceData.map((d: any, i: number) => (
                    <div key={i} className={inEditMode ? 'placeholder-wrapper' : ''}>
                        <Placeholder
                            key={i}
                            data={{ ...data, ...d }}
                            contentPlaceHolderId={CONTENTS_PLACEHOLDER_ID}
                            parentWidgetInstanceId={widgetInstanceId}
                        />
                    </div>
                ))}
        </>
    ) : null;
};

// Lazy-load the component
const LazyComponent = lazy(() => Promise.resolve({ default: DataContextComp }));

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    generatedProperties?: { [key: string]: any };
    dynamicProperties?: Array<{ propertyName: string; propertyType: string; [key: string]: any }>;
}

const DataContextView: FunctionComponent<commonTypes.WidgetInstanceProps> = (props) => {
    const appContext = commonHooks.useAppContext();
    const { document } = appContext;
    const { widgetInstanceId, data } = props;
    const widgetInstances = document?.widgetInstances;
    const widgetInstance = ((widgetInstanceId && widgetInstances) || {})[widgetInstanceId || ''];
    const isLazyLoad = widgetInstance?.properties?.isLazyLoad || (props as any)?.isLazyLoad;

    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(widgetInstanceId);
    const { dynamicProperties = [], generatedProperties = {} } = widgetProperties || {};

    // Generate a set of valid property names from dynamicProperties
    const validPropertyNames = useMemo(() => {
        return new Set(dynamicProperties.map((property) => property.propertyName));
    }, [dynamicProperties]);

    // Filter out generated properties that do not exist in dynamicProperties
    const filteredGeneratedProperties = useMemo(() => {
        return Object.keys(generatedProperties).reduce(
            (acc, propertyName) => {
                if (validPropertyNames.has(propertyName)) {
                    acc[propertyName] = generatedProperties[propertyName];
                }
                return acc;
            },
            {} as { [key: string]: any }
        );
    }, [generatedProperties, validPropertyNames]);

    return (
        <div
            css={css`
                ${dataContextStyles}
            `}
        >
            {isLazyLoad ? (
                <Suspense fallback={null}>
                    <div data-testid="lazy-component">
                        <LazyComponent {...props} />
                    </div>
                </Suspense>
            ) : (
                <DataContextComp
                    widgetInstanceId={widgetInstanceId}
                    data={{ ...data, ...filteredGeneratedProperties }}
                />
            )}
        </div>
    );
};

export default DataContextView;
